import React, { useState } from 'react';
import { api } from '@/api/client';
import { ToggleField } from '@/components/ui';
import {
  duplicateSubscriptionMessage,
  formatApiError,
  isDuplicateSubscriptionError,
} from '@/utils/formatApiError';
import type { Recipient, Search, Subscription } from '@/types';

type Props = {
  subscription: Subscription;
  recipients: Recipient[];
  searches: Search[];
  onClose: () => void;
  onSaved: (message: string) => void;
};

export default function EditSubscriptionModal({
  subscription,
  recipients,
  searches,
  onClose,
  onSaved,
}: Props) {
  const [recipientId, setRecipientId] = useState(subscription.recipient_id ?? subscription.user_id);
  const [searchId, setSearchId] = useState(subscription.search_id);
  const [isEnabled, setIsEnabled] = useState(subscription.is_enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!recipientId || !searchId) {
      setError('Recipient and search are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await api.subscriptions.update(subscription.id, {
        recipient_id: recipientId,
        search_id: searchId,
        is_enabled: isEnabled,
      });
      onSaved(updated.save_message ?? (isEnabled ? 'Saved. New scrape job queued.' : 'Subscription stopped — history preserved'));
      onClose();
    } catch (err) {
      if (isDuplicateSubscriptionError(err)) {
        setError(duplicateSubscriptionMessage(err));
        return;
      }
      setError(formatApiError(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div className="modal card card-elevated" onClick={(e) => e.stopPropagation()} role="dialog">
        <h2>Edit subscription</h2>
        <p className="muted modal-hint">
          Links a recipient to a search. Changing recipient or search does not delete delivery history.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={save}>
          <label>
            Recipient
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              required
            >
              <option value="">Select recipient…</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.chat_id} ({r.display_name})
                </option>
              ))}
            </select>
          </label>
          <label>
            Search
            <select value={searchId} onChange={(e) => setSearchId(e.target.value)} required>
              <option value="">Select search…</option>
              {searches.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <ToggleField
            checked={isEnabled}
            onChange={setIsEnabled}
            label="Enabled (uncheck to stop alerts)"
          />
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
